/**
 * TanStack Query cache keys, scoped by site.
 *
 * Why site-scoped rather than flat: a user's reach changes. An HR user
 * switching the active site filter, or a manager whose assignment moves,
 * must not be served the previous site's employee list from cache. Including
 * the scope in the key makes that structurally impossible rather than a thing
 * somebody has to remember to invalidate.
 *
 * Keys are hierarchical, so invalidating `sites.all(scope)` also clears every
 * filtered list and detail beneath it.
 */
import { useSyncExternalStore } from 'react';

import { useUiStore } from '@/shared/stores/uiStore';
import type { ListParams } from './types';

/** Used when no site filter is active: the query covers the user's whole scope. */
export const WHOLE_SCOPE = '_scope';

/** The active site filter, or the sentinel. Include this in every scoped key. */
export function useSiteScope(): string {
  return useUiStore((state) => state.activeSiteId) ?? WHOLE_SCOPE;
}

/**
 * The same value, read at call time rather than render time.
 *
 * A `useMutation` `onSuccess` closure captures the scope from the render that
 * registered it. If the user switches site while the mutation is in flight,
 * that closure invalidates the *previous* site's keys and the new site's list
 * silently goes stale. Reading through this avoids it.
 */
export function useSiteScopeRef(): () => string {
  const store = useUiStore;
  return () => store.getState().activeSiteId ?? WHOLE_SCOPE;
}

// Re-exported so tests can subscribe without importing the store directly.
export { useSyncExternalStore };

export const queryKeys = {
  auth: {
    // Deliberately not site-scoped: the answer defines the scope.
    me: () => ['auth', 'me'] as const,
  },

  organization: {
    sites: {
      all: (scope: string) => ['organization', scope, 'sites'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'sites', 'list', params] as const,
      /**
       * Paged search for a picker. A sibling of `list`, not a variant of it:
       * a combobox asking for `?search=` ten at a time must not evict the
       * maintainer screen's full list from cache.
       */
      search: (scope: string, params: ListParams) =>
        ['organization', scope, 'sites', 'search', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'sites', 'detail', id] as const,
      history: (scope: string, id: string) =>
        ['organization', scope, 'sites', 'detail', id, 'history'] as const,
    },
    departments: {
      all: (scope: string) => ['organization', scope, 'departments'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'departments', 'list', params] as const,
      search: (scope: string, params: ListParams) =>
        ['organization', scope, 'departments', 'search', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'departments', 'detail', id] as const,
    },
    positions: {
      all: (scope: string) => ['organization', scope, 'positions'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'positions', 'list', params] as const,
      search: (scope: string, params: ListParams) =>
        ['organization', scope, 'positions', 'search', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'positions', 'detail', id] as const,
    },
    positionFamilies: {
      all: (scope: string) => ['organization', scope, 'position-families'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'position-families', 'list', params] as const,
    },
    quickPicks: {
      all: (scope: string) => ['organization', scope, 'quick-picks'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'quick-picks', 'list', params] as const,
    },
    sitePositions: {
      all: (scope: string) => ['organization', scope, 'site-positions'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'site-positions', 'list', params] as const,
      search: (scope: string, params: ListParams) =>
        ['organization', scope, 'site-positions', 'search', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'site-positions', 'detail', id] as const,
    },
    sitePositionReportings: {
      all: (scope: string) => ['organization', scope, 'site-position-reportings'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'site-position-reportings', 'list', params] as const,
    },
    manuals: {
      all: (scope: string) => ['organization', scope, 'manual'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'manual', 'list', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'manual', 'detail', id] as const,
    },
    themes: {
      all: (scope: string) => ['organization', scope, 'themes'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'themes', 'list', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'themes', 'detail', id] as const,
    },
    manuals_version: {
      all: (scope: string) => ['organization', scope, 'manual/versions'] as const,
      list: (scope: string, params: ListParams) =>
        ['organization', scope, 'manual/versions', 'list', params] as const,
      detail: (scope: string, id: string) =>
        ['organization', scope, 'manual/versions', 'detail', id] as const,
    },
    manuals_asset: {
      all: (scope: string) => ['organization', scope, 'manual/assets'] as const,
      download: (scope: string, id: string) =>
        ['organization', scope, 'manual/assets', 'download', id] as const,
      content: (scope: string, id: string) =>
        ['organization', scope, 'manual/assets', 'content', id] as const,
    },
  },

  employees: {
    all: (scope: string) => ['employees', scope, 'employees'] as const,
    list: (scope: string, params: ListParams) =>
      ['employees', scope, 'employees', 'list', params] as const,
    search: (scope: string, params: ListParams) =>
      ['employees', scope, 'employees', 'search', params] as const,
    /** Las filas del listado ligero (`/employees/table/`). */
    table: (scope: string, params: ListParams) =>
      ['employees', scope, 'employees', 'table', params] as const,
    /** Las filas imprimibles de fotocheck (`/employees/id-badges/`). */
    idBadges: (scope: string, params: ListParams) =>
      ['employees', scope, 'employees', 'id-badges', params] as const,
    /** El contador de contratos por terminar (`/employees/contracts-expiring/`). */
    summary: (scope: string) => ['employees', scope, 'employees', 'summary'] as const,
    detail: (scope: string, id: string) => ['employees', scope, 'employees', 'detail', id] as const,
    me: () => ['employees', 'me'] as const,
    documentTypes: {
      all: () => ['employees', 'identity-document-types'] as const,
      list: (params: ListParams) =>
        ['employees', 'identity-document-types', 'list', params] as const,
    },
    contract: {
      download: (scope: string, employeeId: string, assignmentId: string) =>
        [
          'employees',
          scope,
          'employees',
          employeeId,
          'contract',
          assignmentId,
          'download',
        ] as const,
    },
    // The nested assignment history table — paginated, so it needs its own
    // list keys and invalidating `all` clears every page at once.
    assignments: {
      all: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'assignments'] as const,
      list: (scope: string, employeeId: string, params: ListParams) =>
        ['employees', scope, 'employees', employeeId, 'assignments', 'list', params] as const,
    },
    familyDependents: {
      all: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'family-dependents'] as const,
      list: (scope: string, employeeId: string, params: ListParams) =>
        ['employees', scope, 'employees', employeeId, 'family-dependents', 'list', params] as const,
      download: (scope: string, employeeId: string, id: string) =>
        ['employees', scope, 'employees', employeeId, 'family-dependents', 'download', id] as const,
    },
    academicBackgrounds: {
      all: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'academic-backgrounds'] as const,
      list: (scope: string, employeeId: string, params: ListParams) =>
        [
          'employees',
          scope,
          'employees',
          employeeId,
          'academic-backgrounds',
          'list',
          params,
        ] as const,
      download: (scope: string, employeeId: string, id: string) =>
        [
          'employees',
          scope,
          'employees',
          employeeId,
          'academic-backgrounds',
          'download',
          id,
        ] as const,
    },
    // The pension system is a singleton (1-a-1): a single record under the
    // employee, so only an enclosing `all` (for invalidation) and a `detail`
    // query are needed — no paginated list.
    pension: {
      all: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'pension'] as const,
      detail: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'pension', 'detail'] as const,
      download: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'pension', 'download'] as const,
    },
    // The generic expediente files. `download` is a sibling of `list` rather
    // than part of it: the avatar asks for one signed URL and must not evict the
    // (potentially paginated) file list a manage screen is showing.
    files: {
      all: (scope: string, employeeId: string) =>
        ['employees', scope, 'employees', employeeId, 'files'] as const,
      list: (scope: string, employeeId: string, params: ListParams) =>
        ['employees', scope, 'employees', employeeId, 'files', 'list', params] as const,
      download: (scope: string, employeeId: string, id: string) =>
        ['employees', scope, 'employees', employeeId, 'files', 'download', id] as const,
    },
  },

  catalog: {
    // Catalogues are company-wide reference data — deliberately not site-scoped.
    regions: {
      all: () => ['catalog', 'regions'] as const,
      list: (params: ListParams) => ['catalog', 'regions', 'list', params] as const,
    },
    provinces: {
      all: () => ['catalog', 'provinces'] as const,
      list: (params: ListParams) => ['catalog', 'provinces', 'list', params] as const,
    },
    districts: {
      all: () => ['catalog', 'districts'] as const,
      list: (params: ListParams) => ['catalog', 'districts', 'list', params] as const,
    },
    banks: {
      all: () => ['catalog', 'banks'] as const,
      list: (params: ListParams) => ['catalog', 'banks', 'list', params] as const,
    },
    nationalities: {
      all: () => ['catalog', 'nationalities'] as const,
      list: (params: ListParams) => ['catalog', 'nationalities', 'list', params] as const,
    },
    employmentRegimes: {
      all: () => ['catalog', 'employment-regimes'] as const,
      list: (params: ListParams) => ['catalog', 'employment-regimes', 'list', params] as const,
    },
    kinships: {
      all: () => ['catalog', 'kinships'] as const,
      list: (params: ListParams) => ['catalog', 'kinships', 'list', params] as const,
    },
    educationLevels: {
      all: () => ['catalog', 'education-levels'] as const,
      list: (params: ListParams) => ['catalog', 'education-levels', 'list', params] as const,
    },
    afps: {
      all: () => ['catalog', 'afps'] as const,
      list: (params: ListParams) => ['catalog', 'afps', 'list', params] as const,
    },
    employeeFileTypes: {
      all: () => ['catalog', 'employee-file-types'] as const,
      list: (params: ListParams) => ['catalog', 'employee-file-types', 'list', params] as const,
    },
    terminationReasons: {
      all: () => ['catalog', 'termination-reasons'] as const,
      list: (params: ListParams) => ['catalog', 'termination-reasons', 'list', params] as const,
    },
  },

  configuration: {
    // Catalogues and parameters are company-wide reference data, so they are
    // not site-scoped.
    parameters: () => ['configuration', 'parameters'] as const,
  },

  evaluations: {
    // Campaigns are company-wide, but the list mirrors the scope keying of the
    // other features and the population summary is genuinely scope-dependent
    // (RN-24), so every key here stays site-scoped.
    campaigns: {
      all: (scope: string) => ['evaluations', scope, 'campaigns'] as const,
      list: (scope: string, params: ListParams) =>
        ['evaluations', scope, 'campaigns', 'list', params] as const,
      detail: (scope: string, id: string) =>
        ['evaluations', scope, 'campaigns', 'detail', id] as const,
      wizardDefaults: (scope: string) =>
        ['evaluations', scope, 'campaigns', 'wizard-defaults'] as const,
    },
    populationSummary: (scope: string, targetsKey: string) =>
      ['evaluations', scope, 'population-summary', targetsKey] as const,
    myEvaluations: (scope: string) => ['evaluations', scope, 'my-evaluations'] as const,
    campaignAssignments: (scope: string, campaignId: string) =>
      ['evaluations', scope, 'campaign-assignments', campaignId] as const,
    campaignEvaluations: (scope: string, campaignId: string) =>
      ['evaluations', scope, 'campaign-evaluations', campaignId] as const,
    attemptQuestions: (scope: string, attemptId: string) =>
      ['evaluations', scope, 'attempt-questions', attemptId] as const,
    surveyQuestions: (scope: string, evaluationId: string) =>
      ['evaluations', scope, 'survey-questions', evaluationId] as const,
    attemptDetail: (scope: string, attemptId: string) =>
      ['evaluations', scope, 'attempt-detail', attemptId] as const,
    attemptResult: (scope: string, attemptId: string) =>
      ['evaluations', scope, 'attempt-result', attemptId] as const,
    // Reevaluaciones (oral). `all` es el prefijo común: invalida listas y
    // detalle de un golpe tras iniciar, marcar o finalizar.
    oral: {
      all: (scope: string) => ['evaluations', scope, 'oral'] as const,
      campaigns: (scope: string, params: ListParams) =>
        ['evaluations', scope, 'oral', 'campaigns', params] as const,
      candidates: (scope: string, campaignId: string, params: ListParams) =>
        ['evaluations', scope, 'oral', 'candidates', campaignId, params] as const,
      attempt: (scope: string, attemptId: string) =>
        ['evaluations', scope, 'oral', 'attempt', attemptId] as const,
    },
    tracking: {
      campaigns: (scope: string, params: ListParams) =>
        ['evaluations', scope, 'tracking-campaigns', params] as const,
      manualsOverview: (scope: string, campaignId: string) =>
        ['evaluations', scope, 'tracking-manuals', campaignId] as const,
      workers: (scope: string, campaignId: string, manualId: string, params: ListParams) =>
        ['evaluations', scope, 'tracking-workers', campaignId, manualId, params] as const,
      failedQuestions: (scope: string, campaignId: string, manualId: string, params: ListParams) =>
        ['evaluations', scope, 'tracking-failed-questions', campaignId, manualId, params] as const,
      unansweredQuestions: (
        scope: string,
        campaignId: string,
        manualId: string,
        params: ListParams,
      ) =>
        [
          'evaluations',
          scope,
          'tracking-unanswered-questions',
          campaignId,
          manualId,
          params,
        ] as const,
      surveyResponses: (scope: string, campaignId: string, manualId: string) =>
        ['evaluations', scope, 'tracking-survey-responses', campaignId, manualId] as const,
      surveyAnswers: (scope: string, evaluationId: string) =>
        ['evaluations', scope, 'survey-answers', evaluationId] as const,
    },
  },

  iam: {
    // Roles and permissions grant reach rather than being scoped by it, so
    // their keys are deliberately not site-scoped.
    roles: {
      all: () => ['iam', 'roles'] as const,
      list: (params: ListParams) => ['iam', 'roles', 'list', params] as const,
      detail: (id: string) => ['iam', 'roles', 'detail', id] as const,
    },
    permissions: {
      all: () => ['iam', 'permissions'] as const,
      list: (params: ListParams) => ['iam', 'permissions', 'list', params] as const,
      detail: (id: string) => ['iam', 'permissions', 'detail', id] as const,
    },
    assignments: {
      all: () => ['iam', 'role-assignments'] as const,
      list: (params: ListParams) => ['iam', 'role-assignments', 'list', params] as const,
    },
  },

  staffing: {
    // Staffing requests are company-wide, but the table mirrors the scope
    // keying of the other features so the cache stays consistent when the
    // active site filter changes.
    requests: {
      all: (scope: string) => ['staffing', scope, 'requests'] as const,
      list: (scope: string, params: ListParams) =>
        ['staffing', scope, 'requests', 'list', params] as const,
      detail: (scope: string, id: string) => ['staffing', scope, 'requests', 'detail', id] as const,
    },
  },

  navigation: {
    // Keyed by user, not by site: the server filters the tree by the caller's
    // permissions, so a second account on the same tab must not inherit it.
    menu: (userId: string) => ['navigation', 'menu', userId] as const,
    /** Every user's resolved tree, for invalidation after an edit. */
    menus: () => ['navigation', 'menu'] as const,
    items: {
      all: () => ['navigation', 'items'] as const,
    },
  },

  news: {
    // Novedades son de toda la empresa (no dependen de sede), así que las
    // claves no llevan scope. Un `all()` las invalida todas de una.
    posts: {
      all: () => ['news', 'posts'] as const,
      list: (params: ListParams) => ['news', 'posts', 'list', params] as const,
      detail: (id: string) => ['news', 'posts', 'detail', id] as const,
    },
  },
} as const;
